/* eslint-disable @typescript-eslint/naming-convention */
/**
 * The number of frames-in-flight we guess that our browser & driver uses.
 * We call them staging frames as we use it to decide how long staging/transient memory is alive for.
 */
const STAGING_FRAME_COUNT = 3;
function zero_frame_allocation_data() {
    return {
        number_of_allocations: 0,
        total_size: 0
    };
}
class TrackedMemoryResource {
    constructor() {
        this._persistent_size = 0;
        this._past_frame_allocations = [];
        this.deleted_at_frame_id = null;
        this._current_frame_allocations = zero_frame_allocation_data();
        for (let i = 0; i < STAGING_FRAME_COUNT; i++) {
            this._past_frame_allocations.push(zero_frame_allocation_data());
        }
    }
    new_frame() {
        for (let i = STAGING_FRAME_COUNT - 1; i >= 1; i--) {
            this._past_frame_allocations[i] = this._past_frame_allocations[i - 1];
        }
        this._past_frame_allocations[0] = this._current_frame_allocations;
        this._current_frame_allocations = zero_frame_allocation_data();
    }
    resize(new_size) {
        this._persistent_size = new_size;
    }
    register_upload(size) {
        if (size === 0) {
            // We can guess the driver will early out here, and thus we don't deem it an allocation.
            return;
        }
        this._current_frame_allocations.number_of_allocations += 1;
        this._current_frame_allocations.total_size += size;
    }
    get persistent_size() {
        return this._persistent_size;
    }
    get past_frame_allocations() {
        return this._past_frame_allocations;
    }
    get last_frame_allocation_data() {
        return this._past_frame_allocations[0];
    }
    // Does not include the current frame that's in progress on the CPU
    get_total_transient_size(max_frame_count = STAGING_FRAME_COUNT) {
        let total = 0;
        for (let i = 0; i < Math.min(max_frame_count, this._past_frame_allocations.length); i++) {
            total += this._past_frame_allocations[i].total_size;
        }
        return total;
    }
    get_total_transient_allocation_count(max_frame_count = STAGING_FRAME_COUNT) {
        let total = 0;
        for (let i = 0; i < Math.min(max_frame_count, this._past_frame_allocations.length); i++) {
            total += this._past_frame_allocations[i].number_of_allocations;
        }
        return total;
    }
}
class TrackedTexture extends TrackedMemoryResource {
    constructor() {
        super(...arguments);
        this.levels = 0;
        this.layers = 1;
        this.width = 0;
        this.height = 0;
        this.depth = 1;
        this.dimensionality = 0;
        this.internal_format = 0;
        this.immutable_storage = false;
        this.storage_size_untrackable = false;
    }
    get persistent_size() {
        let levels_overhead = 1;
        for (let i = 1; i < this.levels; i++) {
            levels_overhead += 1.0 / (1 << (this.dimensionality * i));
        }
        return Math.floor(super.persistent_size * levels_overhead * this.layers);
    }
}
class TrackedRenderbuffer extends TrackedMemoryResource {
}
class TrackedBuffer extends TrackedMemoryResource {
}
/**
 * A class that wraps a WebGL context, to track memory usage and transfers.
 * The tracked memory usage is not exact, but should be a fairly good measure.
 * In cases of uncertainty it leans on being pessimistic and assume worst cases
 * 2 types of memory usages are tracked:
 *  - Persistent storage: This is the storage size of your buffers, textures, etc.
 *  - Transient memory: This is the memory used by the driver to queue memory uploads, such as bufferSubData. This memory is kept alive for a few frames, set by STAGING_FRAME_COUNT
 */
export class WebGLMemoryTracker {
    constructor(context, viewer, createBuffer = context.createBuffer.bind(context), deleteBuffer = context.deleteBuffer.bind(context), bindBuffer = context.bindBuffer.bind(context), bufferData = context.bufferData.bind(context), bufferSubData = context.bufferSubData.bind(context), createTexture = context.createTexture.bind(context), deleteTexture = context.deleteTexture.bind(context), bindTexture = context.bindTexture.bind(context), generateMipmap = context.generateMipmap.bind(context), texImage2D = context.texImage2D.bind(context), texSubImage2D = context.texSubImage2D.bind(context), texImage3D = context.texImage3D.bind(context), texSubImage3D = context.texSubImage3D.bind(context), texStorage2D = context.texStorage2D.bind(context), texStorage3D = context.texStorage3D.bind(context), compressedTexImage2D = context.compressedTexImage2D.bind(context), compressedTexImage3D = context.compressedTexImage3D.bind(context), compressedTexSubImage2D = context.compressedTexSubImage2D.bind(context), compressedTexSubImage3D = context.compressedTexSubImage3D.bind(context), createRenderbuffer = context.createRenderbuffer.bind(context), deleteRenderbuffer = context.deleteRenderbuffer.bind(context), bindRenderbuffer = context.bindRenderbuffer.bind(context), renderbufferStorage = context.renderbufferStorage.bind(context), renderbufferStorageMultisample = context.renderbufferStorageMultisample.bind(context)) {
        this.createBuffer = createBuffer;
        this.deleteBuffer = deleteBuffer;
        this.bindBuffer = bindBuffer;
        this.bufferData = bufferData;
        this.bufferSubData = bufferSubData;
        this.createTexture = createTexture;
        this.deleteTexture = deleteTexture;
        this.bindTexture = bindTexture;
        this.generateMipmap = generateMipmap;
        this.texImage2D = texImage2D;
        this.texSubImage2D = texSubImage2D;
        this.texImage3D = texImage3D;
        this.texSubImage3D = texSubImage3D;
        this.texStorage2D = texStorage2D;
        this.texStorage3D = texStorage3D;
        this.compressedTexImage2D = compressedTexImage2D;
        this.compressedTexImage3D = compressedTexImage3D;
        this.compressedTexSubImage2D = compressedTexSubImage2D;
        this.compressedTexSubImage3D = compressedTexSubImage3D;
        this.createRenderbuffer = createRenderbuffer;
        this.deleteRenderbuffer = deleteRenderbuffer;
        this.bindRenderbuffer = bindRenderbuffer;
        this.renderbufferStorage = renderbufferStorage;
        this.renderbufferStorageMultisample = renderbufferStorageMultisample;
        this._currently_bound_textures = new Map();
        this._textures = new Map();
        this._currently_bound_buffers = new Map();
        this._buffers = new Map();
        this._currently_bound_renderbuffers = new Map();
        this._renderbuffers = new Map();
        this._frame_id = 0;
        this._update_visualizer_function_handle = null;
        this._visualizer_element = null;
        // How many seconds between visualizer updates
        this.visualizer_render_time_seconds = 0.001;
        this._last_visualizer_update_timestamp = 0;
        this._context = context;
        this._viewer = viewer;
        viewer.get_viewer_dispose_observable().add_once(() => {
            this.set_visualizer_enabled(false);
        });
        context.createBuffer = this.create_buffer.bind(this);
        context.deleteBuffer = this.delete_buffer.bind(this);
        context.bindBuffer = this.bind_buffer.bind(this);
        context.bufferData = this.buffer_data.bind(this);
        context.bufferSubData = this.buffer_sub_data.bind(this);
        context.createTexture = this.create_texture.bind(this);
        context.deleteTexture = this.delete_texture.bind(this);
        context.bindTexture = this.bind_texture.bind(this);
        context.generateMipmap = this.generate_mipmap.bind(this);
        context.texImage2D = this.tex_image_2d.bind(this);
        context.texSubImage2D = this.tex_sub_image_2d.bind(this);
        context.texImage3D = this.tex_image_3d.bind(this);
        context.texSubImage3D = this.tex_sub_image_3d.bind(this);
        context.texStorage2D = this.tex_storage_2d.bind(this);
        context.texStorage3D = this.tex_storage_3d.bind(this);
        context.compressedTexImage2D = this.compressed_tex_image_2d.bind(this);
        context.compressedTexImage3D = this.compressed_tex_image_3d.bind(this);
        context.compressedTexSubImage2D = this.compressed_tex_sub_image_2d.bind(this);
        context.compressedTexSubImage3D = this.compressed_tex_sub_image_3d.bind(this);
        context.createRenderbuffer = this.create_renderbuffer.bind(this);
        context.deleteRenderbuffer = this.delete_renderbuffer.bind(this);
        context.bindRenderbuffer = this.bind_renderbuffer.bind(this);
        context.renderbufferStorage = this.renderbuffer_storage.bind(this);
        context.renderbufferStorageMultisample = this.renderbuffer_storage_multisample.bind(this);
    }
    new_frame() {
        this._buffers.forEach(buffer => buffer.new_frame());
        this._textures.forEach(texture => texture.new_frame());
        this._frame_id++;
    }
    get_memory_usage_report() {
        const report = {
            staging_frames_accounted_for: STAGING_FRAME_COUNT,
            buffers_count: 0,
            buffers_persistent_size: 0,
            buffers_staging_size: 0,
            buffers_staging_allocation_count: 0,
            textures_count: 0,
            untrackable_textures_count: 0,
            textures_persistent_size: 0,
            textures_staging_size: 0,
            textures_staging_allocation_count: 0,
            renderbuffers_count: 0,
            renderbuffers_persistent_size: 0
        };
        this._buffers.forEach((buffer) => {
            if (buffer.deleted_at_frame_id !== null)
                return;
            report.buffers_count++;
            report.buffers_persistent_size += buffer.persistent_size;
            report.buffers_staging_size += buffer.get_total_transient_size();
            report.buffers_staging_allocation_count += buffer.get_total_transient_allocation_count();
        });
        this._textures.forEach((texture) => {
            if (texture.deleted_at_frame_id !== null)
                return;
            if (texture.storage_size_untrackable) {
                report.untrackable_textures_count++;
            }
            report.textures_count++;
            report.textures_persistent_size += texture.persistent_size;
            report.textures_staging_size += texture.get_total_transient_size();
            report.textures_staging_allocation_count += texture.get_total_transient_allocation_count();
        });
        this._renderbuffers.forEach((renderbuffer) => {
            if (renderbuffer.deleted_at_frame_id !== null)
                return;
            report.renderbuffers_count++;
            report.renderbuffers_persistent_size += renderbuffer.persistent_size;
        });
        return report;
    }
    // Meant for JSON serializing
    get_readable_memory_usage_report() {
        const report = this.get_memory_usage_report();
        const formatter = new Intl.NumberFormat();
        const readable_report = Object.assign({}, report);
        for (const key in report) {
            if (typeof report[key] === 'number') {
                readable_report[key] = formatter.format(report[key]);
            }
        }
        return readable_report;
    }
    get_buffers_total_persistent_size() {
        let size = 0;
        this._buffers.forEach((buffer) => {
            if (buffer.deleted_at_frame_id === null) {
                size += buffer.persistent_size;
            }
        });
        return size;
    }
    get_textures_total_persistent_size() {
        let size = 0;
        this._textures.forEach((texture) => {
            if (texture.deleted_at_frame_id === null) {
                size += texture.persistent_size;
            }
        });
        return size;
    }
    get_renderbuffers_total_persistent_size() {
        let size = 0;
        this._renderbuffers.forEach((renderbuffer) => {
            if (renderbuffer.deleted_at_frame_id === null) {
                size += renderbuffer.persistent_size;
            }
        });
        return size;
    }
    get_buffers_persistent_count() {
        let count = 0;
        this._buffers.forEach((buffer) => {
            if (buffer.deleted_at_frame_id === null) {
                count += 1;
            }
        });
        return count;
    }
    get_textures_persistent_count() {
        let count = 0;
        this._textures.forEach((texture) => {
            if (texture.deleted_at_frame_id === null) {
                count += 1;
            }
        });
        return count;
    }
    get_renderbuffers_persistent_count() {
        let count = 0;
        this._renderbuffers.forEach((renderbuffer) => {
            if (renderbuffer.deleted_at_frame_id === null) {
                count += 1;
            }
        });
        return count;
    }
    create_buffer() {
        const buffer = this.createBuffer();
        if (buffer !== null) {
            this._buffers.set(buffer, new TrackedBuffer());
        }
        return buffer;
    }
    delete_buffer(buffer) {
        this.deleteBuffer(buffer);
        if (buffer !== null) {
            const tracked_buffer = this._buffers.get(buffer);
            if (tracked_buffer) {
                tracked_buffer.deleted_at_frame_id = this._frame_id;
            }
        }
    }
    bind_buffer(target, buffer) {
        this.bindBuffer(target, buffer);
        this._currently_bound_buffers.set(target, buffer);
    }
    buffer_data(target, srcData_or_size, usage, srcOffset, length) {
        var _a;
        // Run the actual function first
        if (typeof srcData_or_size === 'number') {
            this.bufferData(target, srcData_or_size, usage);
        }
        else if (srcOffset !== undefined && srcData_or_size !== null) {
            this.bufferData(target, srcData_or_size, usage, srcOffset, length);
        }
        else {
            this.bufferData(target, srcData_or_size, usage);
        }
        // New allocation that decides the buffer's persistent storage size
        let new_size;
        if (typeof srcData_or_size === 'number') {
            new_size = srcData_or_size;
        }
        else if (srcData_or_size !== null) {
            new_size = srcData_or_size.byteLength;
        }
        else if (length !== undefined) {
            new_size = length;
        }
        else {
            new_size = NaN;
            console.error('No data size given for bufferData');
        }
        const webgl_buffer = (_a = this._currently_bound_buffers.get(target)) !== null && _a !== void 0 ? _a : null;
        if (webgl_buffer !== null) {
            const tracked_buffer = this._buffers.get(webgl_buffer);
            tracked_buffer.resize(new_size);
            if (typeof srcData_or_size !== 'number' && srcData_or_size !== null) {
                tracked_buffer.register_upload(new_size);
            }
        }
    }
    buffer_sub_data(target, dstByteOffset, srcData, srcOffset, length) {
        var _a;
        // Run the actual functions first
        if (srcOffset === undefined) {
            this.bufferSubData(target, dstByteOffset, srcData);
        }
        else {
            this.bufferSubData(target, dstByteOffset, srcData, srcOffset, length);
        }
        const webgl_buffer = (_a = this._currently_bound_buffers.get(target)) !== null && _a !== void 0 ? _a : null;
        if (webgl_buffer !== null) {
            const tracked_buffer = this._buffers.get(webgl_buffer);
            if (srcOffset === undefined) {
                tracked_buffer.register_upload(srcData.byteLength);
            }
            else {
                tracked_buffer.register_upload(length !== null && length !== void 0 ? length : srcData.byteLength);
            }
        }
    }
    create_texture() {
        const result = this.createTexture();
        if (result !== null) {
            this._textures.set(result, new TrackedTexture());
        }
        return result;
    }
    delete_texture(texture) {
        this.deleteTexture(texture);
        if (texture !== null) {
            const tracked_texture = this._textures.get(texture);
            if (tracked_texture) {
                tracked_texture.deleted_at_frame_id = this._frame_id;
            }
        }
    }
    bind_texture(target, texture) {
        this.bindTexture(target, texture);
        this._currently_bound_textures.set(target, texture);
    }
    tex_image_2d(target, level, internalformat, width_format, height_type, border_source, format, type, pboOffset_source_pixels, srcOffset) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        if (tracked_texture) {
            tracked_texture.internal_format = internalformat;
            if (target >= this._context.TEXTURE_CUBE_MAP_POSITIVE_X && target <= this._context.TEXTURE_CUBE_MAP_POSITIVE_Z) {
                tracked_texture.layers = 6;
            }
            else {
                tracked_texture.layers = 1;
            }
            tracked_texture.dimensionality = 2;
            tracked_texture.levels = Math.max(level + 1, tracked_texture.levels);
            if (typeof border_source === 'number') {
                const stored_data_size = get_pixel_size(this._context, internalformat) * width_format * height_type;
                if (level === 0) {
                    tracked_texture.resize(stored_data_size);
                    tracked_texture.width = width_format;
                    tracked_texture.height = height_type;
                    tracked_texture.depth = 1;
                }
                if (pboOffset_source_pixels !== null && typeof pboOffset_source_pixels !== 'number') {
                    tracked_texture.register_upload(stored_data_size);
                }
            }
            else if (level === 0) {
                if (typeof border_source['width'] === 'number' && typeof border_source['height'] === 'number') {
                    tracked_texture.resize(get_pixel_size(this._context, internalformat) * border_source['width'] * border_source['height']);
                }
                else {
                    // Untrackable :(
                    tracked_texture.storage_size_untrackable = true;
                    tracked_texture.resize(0);
                    tracked_texture.width = 0;
                    tracked_texture.height = 0;
                }
            }
        }
        if (typeof border_source === 'number') {
            if (format === undefined || type === undefined || pboOffset_source_pixels === undefined) {
                console.error('Invalid memory tracker overload implementation');
            }
            else if (srcOffset === undefined) {
                if (typeof pboOffset_source_pixels === 'number') {
                    console.error('Invalid memory tracker overload implementation');
                }
                else if (pboOffset_source_pixels !== undefined) {
                    if (pboOffset_source_pixels === null) {
                        this.texImage2D(target, level, internalformat, width_format, height_type, border_source, format, type, null);
                    }
                    else {
                        // Here we have to cast as any because Typescript doesn't understand
                        this.texImage2D(target, level, internalformat, width_format, height_type, border_source, format, type, pboOffset_source_pixels);
                    }
                }
            }
            else {
                if (pboOffset_source_pixels === null) {
                    console.error('Invalid memory tracker overload implementation');
                }
                this.texImage2D(target, level, internalformat, width_format, height_type, border_source, format, type, pboOffset_source_pixels, srcOffset);
            }
        }
        else {
            this.texImage2D(target, level, internalformat, width_format, height_type, border_source);
        }
    }
    tex_sub_image_2d(target, level, xoffset, yoffset, width_format, height_type, format_source, type, source_srcData_pboOffset_pixels, srcOffset) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        if (tracked_texture) {
            if (typeof format_source === 'number') {
                tracked_texture.register_upload(get_pixel_size(this._context, tracked_texture.internal_format) * width_format * height_type);
            }
            else if (typeof format_source['width'] === 'number' && typeof format_source['height'] === 'number') {
                tracked_texture.register_upload(get_pixel_size(this._context, tracked_texture.internal_format) * format_source['width'] * format_source['height']);
            }
            else {
                // Untrackable :(
            }
        }
        if (typeof format_source !== 'number') {
            this.texSubImage2D(target, level, xoffset, yoffset, width_format, height_type, format_source);
        }
        else {
            if (type === undefined) {
                console.error('Invalid mock overload implementation');
            }
            else if (srcOffset !== undefined) {
                if (source_srcData_pboOffset_pixels === null) {
                    console.error('Invalid mock overload implementation');
                }
                this.texSubImage2D(target, level, xoffset, yoffset, width_format, height_type, format_source, type, source_srcData_pboOffset_pixels, srcOffset);
            }
            else if (typeof source_srcData_pboOffset_pixels === 'number') {
                this.texSubImage2D(target, level, xoffset, yoffset, width_format, height_type, format_source, type, source_srcData_pboOffset_pixels);
            }
            else {
                // Here we have to cast as any because Typescript doesn't understand
                this.texSubImage2D(target, level, xoffset, yoffset, width_format, height_type, format_source, type, source_srcData_pboOffset_pixels);
            }
        }
    }
    tex_image_3d(target, level, internalformat, width, height, depth, border, format, type, srcData, srcOffset) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        if (tracked_texture) {
            tracked_texture.internal_format = internalformat;
            tracked_texture.dimensionality = 3;
            tracked_texture.layers = 1;
            tracked_texture.levels = Math.max(level + 1, tracked_texture.levels);
            if (level === 0) {
                tracked_texture.width = width;
                tracked_texture.height = height;
                tracked_texture.depth = depth;
                tracked_texture.resize(get_pixel_size(this._context, internalformat) * width * height * depth);
            }
        }
        if (srcOffset !== undefined) {
            if (typeof srcData === 'number') {
                console.error('Invalid memory tracker overload implementation');
            }
            else if (srcData === null) {
                console.error('Invalid memory tracker overload implementation');
            }
            else {
                this.texImage3D(target, level, internalformat, width, height, depth, border, format, type, srcData, srcOffset);
                tracked_texture === null || tracked_texture === void 0 ? void 0 : tracked_texture.register_upload(get_pixel_size(this._context, internalformat) * width * height * depth);
            }
        }
        else {
            // Here we have to cast as any because Typescript doesn't understand
            this.texImage3D(target, level, internalformat, width, height, depth, border, format, type, srcData);
        }
    }
    tex_sub_image_3d(target, level, xoffset, yoffset, zoffset, width, height, depth, format, type, srcData, srcOffset) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        if (tracked_texture) {
            tracked_texture.register_upload(get_pixel_size(this._context, tracked_texture.internal_format) * width * height * depth);
        }
        if (srcOffset !== undefined) {
            if (typeof srcData === 'number') {
                console.error('Invalid mock overload implementation');
            }
            else {
                return this.texSubImage3D(target, level, xoffset, yoffset, zoffset, width, height, depth, format, type, srcData, srcOffset);
            }
        }
        else {
            // Here we have to cast as any because Typescript doesn't understand
            return this.texSubImage3D(target, level, xoffset, yoffset, zoffset, width, height, depth, format, type, srcData);
        }
    }
    tex_storage_2d(target, levels, internalformat, width, height) {
        this.texStorage2D(target, levels, internalformat, width, height);
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        if (tracked_texture) {
            tracked_texture.internal_format = internalformat;
            tracked_texture.layers = target === this._context.TEXTURE_CUBE_MAP ? 6 : 1;
            tracked_texture.immutable_storage = true;
            tracked_texture.dimensionality = 2;
            tracked_texture.levels = levels;
            tracked_texture.width = width;
            tracked_texture.height = height;
            tracked_texture.depth = 1;
            tracked_texture.resize(get_pixel_size(this._context, internalformat) * width * height);
        }
    }
    tex_storage_3d(target, levels, internalformat, width, height, depth) {
        this.texStorage3D(target, levels, internalformat, width, height, depth);
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        if (tracked_texture) {
            tracked_texture.internal_format = internalformat;
            tracked_texture.layers = 1;
            tracked_texture.immutable_storage = true;
            tracked_texture.dimensionality = 3;
            tracked_texture.levels = levels;
            tracked_texture.width = width;
            tracked_texture.height = height;
            tracked_texture.depth = depth;
            tracked_texture.resize(get_pixel_size(this._context, internalformat) * width * height * depth);
        }
    }
    compressed_tex_image_2d(target, level, internalformat, width, height, border, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        let image_size_bytes = 0;
        if (typeof srcData_or_imageSize === 'number' && srcOffset_or_offset !== undefined) {
            this.compressedTexImage2D(target, level, internalformat, width, height, border, srcData_or_imageSize, srcOffset_or_offset);
            image_size_bytes = srcData_or_imageSize;
        }
        else if (typeof srcData_or_imageSize !== 'number') {
            this.compressedTexImage2D(target, level, internalformat, width, height, border, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride);
            image_size_bytes = srcLengthOverride !== null && srcLengthOverride !== void 0 ? srcLengthOverride : srcData_or_imageSize.byteLength;
        }
        else {
            console.warn('WebGL memory tracker invalid overload');
        }
        if (level === 0 && tracked_texture) {
            tracked_texture.internal_format = internalformat;
            tracked_texture.width = width;
            tracked_texture.height = height;
            tracked_texture.depth = 1;
            tracked_texture.dimensionality = 2;
            if (target >= this._context.TEXTURE_CUBE_MAP_POSITIVE_X && target <= this._context.TEXTURE_CUBE_MAP_POSITIVE_Z) {
                tracked_texture.layers = 6;
            }
            else {
                tracked_texture.layers = 1;
            }
            tracked_texture.resize(image_size_bytes);
        }
    }
    compressed_tex_sub_image_2d(target, level, xoffset, yoffset, width, height, format, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        let transferred_image_size_bytes = 0;
        if (typeof srcData_or_imageSize === 'number' && srcOffset_or_offset !== undefined) {
            this.compressedTexSubImage2D(target, level, xoffset, yoffset, width, height, format, srcData_or_imageSize, srcOffset_or_offset);
            transferred_image_size_bytes = 0;
        }
        else if (typeof srcData_or_imageSize !== 'number') {
            this.compressedTexSubImage2D(target, level, xoffset, yoffset, width, height, format, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride);
            transferred_image_size_bytes = srcLengthOverride !== null && srcLengthOverride !== void 0 ? srcLengthOverride : srcData_or_imageSize.byteLength;
        }
        else {
            console.warn('WebGL memory tracker invalid overload');
        }
        if (tracked_texture) {
            tracked_texture.levels = Math.max(tracked_texture.levels, level + 1);
            tracked_texture.register_upload(transferred_image_size_bytes);
        }
    }
    compressed_tex_image_3d(target, level, internalformat, width, height, depth, border, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        let image_size_bytes = 0;
        if (typeof srcData_or_imageSize === 'number' && srcOffset_or_offset !== undefined) {
            this.compressedTexImage3D(target, level, internalformat, width, height, depth, border, srcData_or_imageSize, srcOffset_or_offset);
            image_size_bytes = srcData_or_imageSize;
        }
        else if (typeof srcData_or_imageSize !== 'number') {
            this.compressedTexImage3D(target, level, internalformat, width, height, depth, border, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride);
            image_size_bytes = srcLengthOverride !== null && srcLengthOverride !== void 0 ? srcLengthOverride : srcData_or_imageSize.byteLength;
        }
        else {
            console.warn('WebGL memory tracker invalid overload');
        }
        if (level === 0 && tracked_texture) {
            tracked_texture.internal_format = internalformat;
            tracked_texture.width = width;
            tracked_texture.height = height;
            tracked_texture.depth = depth;
            tracked_texture.dimensionality = 3;
            tracked_texture.layers = 1;
            tracked_texture.resize(image_size_bytes);
        }
    }
    compressed_tex_sub_image_3d(target, level, xoffset, yoffset, zoffset, width, height, depth, format, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride) {
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        let transferred_image_size_bytes = 0;
        if (typeof srcData_or_imageSize === 'number' && srcOffset_or_offset !== undefined) {
            this.compressedTexSubImage3D(target, level, xoffset, yoffset, zoffset, width, height, depth, format, srcData_or_imageSize, srcOffset_or_offset);
            transferred_image_size_bytes = 0;
        }
        else if (typeof srcData_or_imageSize !== 'number') {
            this.compressedTexSubImage3D(target, level, xoffset, yoffset, zoffset, width, height, depth, format, srcData_or_imageSize, srcOffset_or_offset, srcLengthOverride);
            transferred_image_size_bytes = srcLengthOverride !== null && srcLengthOverride !== void 0 ? srcLengthOverride : srcData_or_imageSize.byteLength;
        }
        else {
            console.warn('WebGL memory tracker invalid overload');
        }
        if (tracked_texture) {
            tracked_texture.levels = Math.max(tracked_texture.levels, level + 1);
            tracked_texture.register_upload(transferred_image_size_bytes);
        }
    }
    generate_mipmap(target) {
        this.generateMipmap(target);
        const current_texture = this._currently_bound_textures.get(target);
        const tracked_texture = current_texture ? this._textures.get(current_texture) : null;
        if (tracked_texture && !tracked_texture.immutable_storage) {
            tracked_texture.levels = 0;
            let width = tracked_texture.width;
            let height = tracked_texture.height;
            let depth = tracked_texture.depth;
            while (width > 0 || height > 0 || depth > 0) {
                tracked_texture.levels++;
                width = Math.floor(width / 2);
                height = Math.floor(height / 2);
                depth = Math.floor(depth / 2);
            }
        }
    }
    create_renderbuffer() {
        const renderbuffer = this.createRenderbuffer();
        if (renderbuffer) {
            this._renderbuffers.set(renderbuffer, new TrackedRenderbuffer());
        }
        return renderbuffer;
    }
    delete_renderbuffer(renderbuffer) {
        this.deleteRenderbuffer(renderbuffer);
        if (renderbuffer !== null) {
            const tracked_renderbuffer = this._renderbuffers.get(renderbuffer);
            if (tracked_renderbuffer) {
                tracked_renderbuffer.deleted_at_frame_id = this._frame_id;
            }
        }
    }
    bind_renderbuffer(target, renderbuffer) {
        this.bindRenderbuffer(target, renderbuffer);
        this._currently_bound_renderbuffers.set(target, renderbuffer);
    }
    renderbuffer_storage(target, internalformat, width, height) {
        this.renderbufferStorage(target, internalformat, width, height);
        const current_renderbuffer = this._currently_bound_renderbuffers.get(target);
        const tracked_renderbuffer = current_renderbuffer ? this._renderbuffers.get(current_renderbuffer) : null;
        if (tracked_renderbuffer) {
            tracked_renderbuffer.resize(get_pixel_size(this._context, internalformat) * width * height);
        }
    }
    renderbuffer_storage_multisample(target, samples, internalformat, width, height) {
        this.renderbufferStorageMultisample(target, samples, internalformat, width, height);
        const current_renderbuffer = this._currently_bound_renderbuffers.get(target);
        const tracked_renderbuffer = current_renderbuffer ? this._renderbuffers.get(current_renderbuffer) : null;
        if (tracked_renderbuffer) {
            tracked_renderbuffer.resize(samples * get_pixel_size(this._context, internalformat) * width * height);
        }
    }
    set_visualizer_enabled(enabled) {
        if (this._visualizer_element === null) {
            this._visualizer_element = document.createElement('div');
            this._visualizer_element.style.zIndex = '10';
            this._visualizer_element.style.position = 'absolute';
            this._visualizer_element.style.left = '0.5em';
            this._visualizer_element.style.top = '3em';
            this._visualizer_element.style.textAlign = 'left';
            this._visualizer_element.style.fontFamily = 'monospace';
            this._visualizer_element.style.fontSize = '12px';
            this._visualizer_element.style.userSelect = 'none';
            this._visualizer_element.style.pointerEvents = 'none';
            this._visualizer_element.style.background = 'rgba(111, 222, 55, 0.95)';
            document.body.appendChild(this._visualizer_element);
        }
        if (enabled) {
            this._visualizer_element.style.display = 'block';
            this._update_visualizer_function_handle = requestAnimationFrame(this.update_visualizer.bind(this));
        }
        else {
            this._visualizer_element.style.display = 'none';
            if (this._update_visualizer_function_handle !== null) {
                cancelAnimationFrame(this._update_visualizer_function_handle);
            }
            this._update_visualizer_function_handle = null;
        }
    }
    update_visualizer() {
        // This code is a real eye-sore, and we should find a better way to do this in the future.
        // Convert milliseconds to seconds
        const timestamp = performance.now() / 1000.0;
        if (timestamp - this._last_visualizer_update_timestamp >= this.visualizer_render_time_seconds) {
            this._last_visualizer_update_timestamp = timestamp;
            const style = `
        border-spacing: 32px 6px;
      `;
            let html = '<table style="' + style + '"><tr>';
            html += '<th>WebGL Memory Usage</th><th>Value</th>';
            html += '</tr>';
            const report = this.get_readable_memory_usage_report();
            for (const key in report) {
                const value = report[key];
                html += '<tr><td>' + key + '</td>';
                html += '<td>' + value + '</td>';
                html += '</tr>';
            }
            html += '</table>';
            this._visualizer_element.innerHTML = html;
        }
        if (this._update_visualizer_function_handle) {
            this._update_visualizer_function_handle = requestAnimationFrame(this.update_visualizer.bind(this));
        }
    }
}
/**
 * Covers WebGL2 formats described:
 *    here: https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/renderbufferStorage
 *    and here: https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/texImage2D
 * @returns The size of a pixel in the given sized format, in bytes.
 */
function get_pixel_size(gl, internalformat) {
    switch (internalformat) {
        case gl.R8:
        case gl.R8_SNORM:
        case gl.R8I:
        case gl.R8UI:
        case gl.ALPHA:
        case gl.LUMINANCE:
        case gl.STENCIL_INDEX8:
            return 1;
        case gl.RG8:
        case gl.RG8_SNORM:
        case gl.RGBA4:
        case gl.RGB5_A1:
        case gl.RGB565:
        case gl.R16F:
        case gl.R16I:
        case gl.R16UI:
        case gl.RG8I:
        case gl.RG8UI:
        case gl.LUMINANCE_ALPHA:
        case gl.DEPTH_COMPONENT16:
            return 2;
        case gl.RGB:
        case gl.RGB8:
        case gl.RGB8_SNORM:
        case gl.SRGB8:
        case gl.SRGB:
        case gl.RGB8I:
        case gl.RGB8UI:
        case gl.DEPTH_COMPONENT24:
            return 4; // Disputable, this is either 3 or 4, depending on alignment
        case gl.RGBA:
        case gl.RGBA8:
        case gl.RGBA8_SNORM:
        case gl.RGB10_A2:
        case gl.RGB10_A2UI:
        case gl.SRGB8_ALPHA8:
        case gl.RG16F:
        case gl.R32F:
        case gl.R11F_G11F_B10F:
        case gl.RGB9_E5:
        case gl.R32I:
        case gl.R32UI:
        case gl.RG16I:
        case gl.RG16UI:
        case gl.RGBA8I:
        case gl.RGBA8UI:
        case gl.DEPTH_COMPONENT: // Actually unknown, but a good upper-bound guess
        case gl.DEPTH_STENCIL: // Actually unknown, but a good upper-bound guess
        case gl.DEPTH_COMPONENT32F:
        case gl.DEPTH24_STENCIL8:
            return 4;
        case gl.DEPTH32F_STENCIL8:
            return 5; // Really disputable (alignment). Assumption for 5 would be that the depth and stencil parts are stored separately.
        case gl.RGB16F:
        case gl.RGB16I:
        case gl.RGB16UI:
            return 8; // Disputable. Could be 6, but guessing 8 due to RGB usually being RGBA in real life.
        case gl.RGBA16F:
        case gl.RG32F:
        case gl.RG32I:
        case gl.RG32UI:
        case gl.RGBA16I:
        case gl.RGBA16UI:
            return 8;
        case gl.RGB32F:
        case gl.RGB32I:
        case gl.RGB32UI:
            return 16; // Disputable, could be 12 but probably 16 due to usually being RGBA in practice.
        case gl.RGBA32F:
        case gl.RGBA32I:
        case gl.RGBA32UI:
            return 16;
    }
    console.error('Unknown internalFormat parameter: ' + internalformat);
    return 0;
}
//# sourceMappingURL=WebGLMemoryTracker.js.map